import { useEffect, useState } from "react";
import { IoShareOutline, IoDownloadOutline, IoAddCircleOutline, IoClose } from "react-icons/io5";
import { isStandalone } from "@/push";

const DISMISS_KEY = "tf_install_dismissed_v1";
const DISMISS_DAYS = 7;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isAndroid = () => /Android/i.test(navigator.userAgent);

const isMobileBrowser = () => isIOS() || isAndroid();

const wasDismissedRecently = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export const InstallAppBanner = () => {
  const [visible, setVisible] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (isStandalone()) return;
    if (!isMobileBrowser()) return;
    if (wasDismissedRecently()) return;

    // Delay banner so it doesn't compete with the onboarding
    const showTimer = setTimeout(() => setVisible(true), 1500);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      clearTimeout(showTimer);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
    setVisible(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      // Android / Chromium
      deferredPrompt.prompt();
      try {
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") setVisible(false);
      } catch { /* noop */ }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      // iOS Safari has no prompt: show manual instructions
      setIosGuideOpen(true);
    } else {
      // Android but no prompt event yet: show hint
      setIosGuideOpen(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div className="tf-install-banner" data-testid="install-app-banner" role="dialog" aria-label="Installa l'app">
        <div className="tf-install-icon" aria-hidden="true">
          <IoDownloadOutline size={22} />
        </div>
        <div className="tf-install-body">
          <div className="tf-install-title">Installa The First</div>
          <div className="tf-install-sub">
            Aggiungila alla schermata Home per notifiche push e apertura più veloce.
          </div>
        </div>
        <button
          type="button"
          data-testid="install-app-btn"
          className="tf-install-cta"
          onClick={install}
        >
          Installa
        </button>
        <button
          type="button"
          data-testid="install-app-dismiss"
          className="tf-install-close"
          onClick={dismiss}
          aria-label="Chiudi"
        >
          <IoClose size={18} />
        </button>
      </div>

      {iosGuideOpen && (
        <div
          className="tf-install-guide-overlay"
          data-testid="install-guide-overlay"
          onClick={() => setIosGuideOpen(false)}
        >
          <div
            className="tf-install-guide-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Come installare"
          >
            <div className="tf-drawer-handle" />
            <h3 className="tf-h2" style={{ margin: "6px 0 4px" }}>
              Aggiungi The First alla Home
            </h3>
            <p className="tf-small" style={{ color: "var(--tf-muted)", marginTop: 0 }}>
              {isIOS() ? "Su iPhone/iPad (Safari):" : "Sul tuo dispositivo:"}
            </p>
            <ol className="tf-install-guide-steps">
              <li>
                <span className="tf-install-guide-icon"><IoShareOutline size={18} /></span>
                <span>Tocca il pulsante <b>Condividi</b> nella barra del browser</span>
              </li>
              <li>
                <span className="tf-install-guide-icon"><IoAddCircleOutline size={18} /></span>
                <span>Scorri e seleziona <b>Aggiungi alla schermata Home</b></span>
              </li>
              <li>
                <span className="tf-install-guide-icon"><IoDownloadOutline size={18} /></span>
                <span>Tocca <b>Aggiungi</b> in alto a destra</span>
              </li>
            </ol>
            <button
              type="button"
              data-testid="install-guide-close"
              className="tf-btn-primary"
              onClick={() => setIosGuideOpen(false)}
              style={{ marginTop: 12 }}
            >
              Ho capito
            </button>
          </div>
        </div>
      )}
    </>
  );
};
